import React, { useMemo, useState } from "react";
import { Images } from "../utils/imageConstants";

type NullableDate = Date | null;

export interface CalendarProps {
  initialStartDate?: NullableDate;
  initialEndDate?: NullableDate;
  onApply?: (range: { startDate: NullableDate; endDate: NullableDate }) => void;
  years?: number[];
}

const Calendar: React.FC<CalendarProps> = ({
  initialStartDate = null,
  initialEndDate = null,
  onApply = () => {},
  years: yearsProp,
}) => {
  const years = useMemo(() => {
    if (yearsProp && yearsProp.length > 0) return yearsProp;
    const cy = new Date().getFullYear();
    return Array.from({ length: 14 }, (_, i) => cy + 2 - i);
  }, [yearsProp]);

  const today = new Date();
  const [yearDropdown, setYearDropdown] = useState<boolean>(false);

  // View state (what month/year grid is shown)
  const initialView = initialStartDate ?? today;
  const [viewYear, setViewYear] = useState<number>(initialView.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initialView.getMonth()); // 0-11

  // Selection (range)
  const [startDate, setStartDate] = useState<NullableDate>(initialStartDate);
  const [endDate, setEndDate] = useState<NullableDate>(initialEndDate);

  // ------- date utils -------
  const startOfDay = (d: Date): Date =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const sameDay = (a: NullableDate, b: NullableDate): boolean =>
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isBefore = (a: Date, b: Date): boolean =>
    startOfDay(a).getTime() < startOfDay(b).getTime();

  const isBetween = (d: Date, a: NullableDate, b: NullableDate): boolean => {
    if (!a || !b) return false;
    const x = +startOfDay(d);
    const min = +startOfDay(a);
    const max = +startOfDay(b);
    return x > min && x < max;
  };

  const isWeekend = (d: Date): boolean => {
    const day = d.getDay(); // 0 Sun, 6 Sat
    return day === 0 || day === 6;
  };

  const monthLabel = useMemo<string>(() => {
    const d = new Date(viewYear, viewMonth, 1);
    return d.toLocaleString("default", { month: "long", year: "numeric" });
  }, [viewYear, viewMonth]);

  // Build a 6x7 grid (42 cells) starting Monday
  const daysGrid = useMemo<Array<{ date: Date; inCurrentMonth: boolean }>>(() => {
    const first = new Date(viewYear, viewMonth, 1);
    // JS getDay(): 0 Sun ... 6 Sat; we want Monday=0 ... Sunday=6
    const jsDow = first.getDay(); // 0 Sun..6 Sat
    const mondayIndex = (jsDow + 6) % 7; // 0..6 (Mon=0)
    const gridStart = new Date(viewYear, viewMonth, 1 - mondayIndex);

    const cells: Array<{ date: Date; inCurrentMonth: boolean }> = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push({
        date: d,
        inCurrentMonth: d.getMonth() === viewMonth,
      });
    }
    return cells;
  }, [viewYear, viewMonth]);

  const goPrevMonth = (): void => {
    setYearDropdown(false);
    const prev = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(prev.getFullYear());
    setViewMonth(prev.getMonth());
  };

  const goNextMonth = (): void => {
    setYearDropdown(false);
    const next = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const onPickYear = (y: number): void => {
    setViewYear(y);
    setYearDropdown(false);
  };

  const onClickDay = (d: Date): void => {
    const date = startOfDay(d);
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (startDate && !endDate) {
      if (isBefore(date, startDate)) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    }
  };

  const onReset = (): void => {
    setStartDate(null);
    setEndDate(null);
  };

  const onApplyClick = (): void => {
    onApply({ startDate, endDate });
  };

  // Given a date, return the **outer day cell** class that matches your SCSS
  const outerDayClass = (d: Date /* inCurrentMonth unused but kept for parity */): string => {
    const isToday = sameDay(d, today);
    const selectedStart = !!startDate && sameDay(d, startDate);
    const selectedEnd = !!endDate && sameDay(d, endDate);
    const inRange = isBetween(d, startDate, endDate);

    if (selectedStart && !!endDate) return "day-of-a-weekother-month-ran2"; // range start
    if (selectedEnd && !!startDate) return "day-of-a-weekweekday-rangee";   // range end
    if (inRange) return isWeekend(d) ? "day-of-a-weekweekend-rangem" : "day-of-a-weekweekday-rangem";
    if (isToday) return "day-of-a-weektoday-rangenon";
    return "day-of-a-weekother-month-ran";
  };

  // Inner number circle wrapper class (filled purple when selected, hollow otherwise)
  const innerCircleClass = (d: Date): string => {
    const selected =
      (!!startDate && sameDay(d, startDate)) ||
      (!!endDate && sameDay(d, endDate));
    return selected ? "auto-layout-vertical18" : "auto-layout-vertical16";
  };

  const weekHeaders: readonly string[] = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  return (
    <div className="calendar">
      <div className="select">
        <div
          className="auto-layout-vertical"
          onClick={() => setYearDropdown((v) => !v)}
          role="button"
          aria-label="Toggle year dropdown"
        >
          <div className="year">{viewYear}</div>
        </div>
        <div className="icon">
          <img className="vector-icon" alt="" src={Images.calendar_dropdown_icon} />
        </div>

        {yearDropdown && (
          <div className="tournament-dropdown-points-tab">
            <div className="filter-options">
              {years.map((y) => (
                <div
                  className="filter-option"
                  key={y}
                  onClick={() => onPickYear(y)}
                  role="button"
                >
                  <div className="filter-option-text">{y}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="popup">
        <div className="auto-layout-vertical-parent">
          <div className="calendar-auto-layout-vertical">
            <div className="auto-layout-vertical">
              <div className="auto-layout-vertical3">
                <div
                  className="year"
                  onClick={() => {
                    const t = new Date();
                    setViewYear(t.getFullYear());
                    setViewMonth(t.getMonth());
                  }}
                  role="button"
                >
                  Today
                </div>
              </div>
              <div className="auto-layout-vertical4">
                <div
                  className="yesterday"
                  onClick={() => {
                    const t = new Date();
                    const yest = new Date(t);
                    yest.setDate(t.getDate() - 1);
                    setStartDate(startOfDay(yest));
                    setEndDate(startOfDay(yest));
                    setViewYear(yest.getFullYear());
                    setViewMonth(yest.getMonth());
                  }}
                  role="button"
                >
                  Yesterday
                </div>
              </div>
              <div className="auto-layout-vertical4">
                <div
                  className="yesterday"
                  onClick={() => {
                    const end = startOfDay(new Date());
                    const start = new Date(end);
                    start.setDate(end.getDate() - 6);
                    setStartDate(start);
                    setEndDate(end);
                    setViewYear(end.getFullYear());
                    setViewMonth(end.getMonth());
                  }}
                  role="button"
                >
                  Last week
                </div>
              </div>
              <div className="auto-layout-vertical4">
                <div
                  className="yesterday"
                  onClick={() => {
                    const end = startOfDay(new Date());
                    const start = new Date(end);
                    start.setMonth(end.getMonth() - 1);
                    setStartDate(start);
                    setEndDate(end);
                    setViewYear(end.getFullYear());
                    setViewMonth(end.getMonth());
                  }}
                  role="button"
                >
                  Last month
                </div>
              </div>
              <div className="auto-layout-vertical4">
                <div
                  className="yesterday"
                  onClick={() => {
                    const end = startOfDay(new Date());
                    const start = new Date(end);
                    start.setMonth(end.getMonth() - 3);
                    setStartDate(start);
                    setEndDate(end);
                    setViewYear(end.getFullYear());
                    setViewMonth(end.getMonth());
                  }}
                  role="button"
                >
                  Last quarter
                </div>
              </div>
            </div>
          </div>

          <div className="auto-layout-vertical8">
            {/* header row: month + arrows */}
            <div className="auto-layout-horizontal">
              <div className="month">{monthLabel}</div>
              <div className="calendar-auto-layout-horizontal">
                <div
                  className="date"
                  role="button"
                  onClick={goPrevMonth}
                  aria-label="Previous month"
                >
                  <div className="icon">
                    <img className="chevron-left-icon" alt="" src={Images.calendar_left_icon} />
                  </div>
                </div>
                <div
                  className="date"
                  role="button"
                  onClick={goNextMonth}
                  aria-label="Next month"
                >
                  <div className="icon">
                    <img className="chevron-left-icon" alt="" src={Images.calendar_right_icon} />
                  </div>
                </div>
              </div>
            </div>

            {/* week headers */}
            <div className="auto-layout-horizontal2">
              {weekHeaders.map((w) => (
                <div className="auto-layout-vertical9" key={w}>
                  <div className="yesterday">{w}</div>
                </div>
              ))}
            </div>

            {/* days grid (6 rows of 7) */}
            <div className="date-container">
              {Array.from({ length: 6 }).map((_, rowIdx) => {
                const rowDays = daysGrid.slice(rowIdx * 7, rowIdx * 7 + 7);
                const rowClass =
                  rowIdx === 0 || rowIdx === 5
                    ? "auto-layout-horizontal2"
                    : "auto-layout-horizontal4";

                return (
                  <div className={rowClass} key={rowIdx}>
                    {rowDays.map(({ date }) => {
                      const outerClass = outerDayClass(date);
                      const innerClass = innerCircleClass(date);
                      const num = date.getDate();
                      const selected =
                        (!!startDate && sameDay(date, startDate)) ||
                        (!!endDate && sameDay(date, endDate));
                      const showDot =
                        selected || isBetween(date, startDate, endDate);

                      return (
                        <div
                          className={outerClass}
                          key={date.toISOString()}
                          onClick={() => onClickDay(date)}
                          role="button"
                          aria-label={`Select ${date.toDateString()}`}
                        >
                          <div className={innerClass}>
                            {/* day number */}
                            <div className={selected ? "year" : "yesterday"}>
                              {num}
                            </div>
                            {/* center dot (kept to match your DOM) */}
                            {showDot && (
                              <div className="auto-layout-vertical-child" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="reset-parent">
          <div className="reset" role="button" onClick={onReset}>
            <div className="year">Reset</div>
          </div>
          <div className="frame-child" />
          <div className="reset" role="button" onClick={onApplyClick}>
            <div className="year">Apply</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
